// @ts-strict-ignore
import React, { useContext, useEffect, useMemo } from 'react';
import { Box, ButtonVariant, Spinner } from '@usealloy/component-library';
import ThresholdsTab from '~/dashboard/views/workflows/editor/components/NodeEditorDrawer/ThresholdsTab';
import type { Action, ThresholdResults } from '~/dashboard/views/workflows/store/engine/types';
import {
  StyledSearchBoxContainer,
  StyledSearchTriggerContainer,
  StyledSearchTriggerRow,
  StyledSectionHeader,
  StyledWorkflowCanvasContainer,
} from './styles';
import { UserContext } from '~/dashboard/context';
import { ContainerType } from '~/dashboard/views/workflows/editor/components/NodeEditorDrawer/ThresholdsTab/types';
import { useEditorAccess } from 'flow/editor/context';
import WorkflowCanvas from 'flow/editor/components/WorkflowCanvas';
import { SearchBox, SearchTrigger } from 'flow/editor/components/Search';
import { EditorMode } from 'flow/editor/context/constants';
import { EditorContainerType } from 'flow/store/redux/constants';
import { selectEditorContainerType, selectSimpleIslandMode } from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';

type TagRuleProps = {
  action: Action;
  thresholdResults: ThresholdResults;
  loading: boolean;
  canViewRules: boolean;
};

const TagRule = ({
  action,
  thresholdResults,
  loading,
  canViewRules,
}: TagRuleProps) => {
  const { featureFlags } = useContext(UserContext);
  const islandMode = useAppSelector(selectSimpleIslandMode);
  const containerType = useAppSelector(selectEditorContainerType);
  const isPageContainer = containerType === EditorContainerType.PAGE;

  const { setEditorMode } = useEditorAccess();
  useEffect(() => {
    setEditorMode(EditorMode.ReadOnly);
  }, [setEditorMode]);

  const isWorkflowCanvasView = featureFlags?.ruleExplainabilityPhase >= 4;

  // Page containerType: trigger sits in normal flow above the canvas (StyledSearchTriggerRow).
  const pageSearchTrigger = isPageContainer && (
    <StyledSearchTriggerRow islandMode={islandMode}>
      <SearchTrigger variant={ButtonVariant.tertiary} />
    </StyledSearchTriggerRow>
  );

  // Modal containerType: trigger is absolute positioned over the canvas.
  const modalSearchTrigger = !isPageContainer && (
    <StyledSearchTriggerContainer>
      <SearchTrigger variant={ButtonVariant.tertiary} />
    </StyledSearchTriggerContainer>
  );

  const workflowCanvasView = (
    <StyledWorkflowCanvasContainer direction="column" alignItems="flex-start">
      <StyledSearchBoxContainer containerType={containerType} islandMode={islandMode}>
        <SearchBox withoutContainer />
      </StyledSearchBoxContainer>
      {pageSearchTrigger}
      <WorkflowCanvas readOnly />
      {modalSearchTrigger}
    </StyledWorkflowCanvasContainer>
  );

  const legacyThresholdsView = ( // does not have RE phase >= 4
    <Box direction="column" alignItems="flex-start">
      <ThresholdsTab
        action={action}
        containerType={ContainerType.MODAL}
        thresholdResults={thresholdResults || null}
      />
    </Box>
  );

  const tagRuleContent = isWorkflowCanvasView ? workflowCanvasView : legacyThresholdsView;

  const outputSectionHeader = useMemo(() => {
    if (featureFlags?.ruleExplainabilityPhase > 0) {
      return (
        <StyledSectionHeader
          size={1}
          /**
           * Specific pixel width for output values section
           * See Rule Explainability Figma design
           */
          style={{
            width: '194px',
            minWidth: '194px',
            paddingLeft: 'var(--cl-space-6)',
          }}
        >
          Output
        </StyledSectionHeader>
      );
    }

    return null;
  }, [featureFlags]);

  if (!canViewRules) {
    return null;
  }

  if (isWorkflowCanvasView) {
    return (
      <Box
        direction="column"
        alignItems="flex-start"
        gap={3}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        {loading ? (
          <Box>
            <Spinner />
          </Box>
        ) : tagRuleContent}
      </Box>
    );
  }

  return (
    <Box direction="column" alignItems="flex-start" gap={3}>
      <Box justifyContent="space-between">
        <StyledSectionHeader size={1}>
          Rule
        </StyledSectionHeader>
        {outputSectionHeader}
      </Box>
      {loading || !action ? (
        <Box>
          <Spinner />
        </Box>
      ) : tagRuleContent}
    </Box>
  );
};

export default TagRule;
